import { terminologyApi, type TermMap } from '@/api/terminologyApi'
import { NAMESPACES, type Namespace } from '@/i18n/termKeys'
import i18n from '@/i18n/i18n'
import { toCsv, parseCsv, downloadTextFile } from '@/lib/csv'

const CSV_HEADER = ['namespace', 'key', 'value']
const KNOWN_NS = new Set<string>(NAMESPACES)

export type CsvResult = { ok: boolean; partial?: boolean; messages: string[] }

// Neutralize spreadsheet formulas. Admin-supplied term values that start with
// =/+/-/@ would execute as formulas when the CSV is opened in Excel/Sheets,
// regardless of quoting. Prefixing a single quote forces the cell to be text.
const DANGEROUS_CSV_PREFIX = /^[=+\-@\t\r]/
function escapeSpreadsheetCell(value: string): string {
  return DANGEROUS_CSV_PREFIX.test(value) ? `'${value}` : value
}

/** Download all of a locale's resolved terms as `namespace,key,value` CSV (also a template). */
export async function exportLocaleCsv(locale: string): Promise<void> {
  const rows: string[][] = [CSV_HEADER]
  for (const ns of NAMESPACES) {
    const terms = await terminologyApi.getNamespace(locale, ns)
    for (const [key, value] of Object.entries(terms)) {
      rows.push([ns, key, escapeSpreadsheetCell(value)])
    }
  }
  downloadTextFile(`terminology-${locale}.csv`, toCsv(rows))
}

/**
 * Parse a `namespace,key,value` CSV and save the overrides for `locale`, grouped per
 * namespace. Keys are unrestricted — a row with a brand-new key adds that term. The
 * server validates each save (non-empty value, preserved placeholders, valid key
 * characters). On success the locale's i18next resources are reloaded so labels
 * update live.
 */
export async function importLocaleCsv(locale: string, file: File): Promise<CsvResult> {
  const rows = parseCsv(await file.text())
  if (rows.length === 0) return { ok: false, messages: ['The file is empty'] }

  // Tolerate a header row if present.
  const header = rows[0].map((c) => c.trim().toLowerCase())
  const hasHeader = header.join(',') === CSV_HEADER.join(',')
  const dataRows = hasHeader ? rows.slice(1) : rows

  const byNamespace: Record<string, TermMap> = {}
  const parseErrors: string[] = []
  dataRows.forEach((cols, idx) => {
    const line = idx + (hasHeader ? 2 : 1)
    if (cols.length < 3) {
      parseErrors.push(`Line ${line}: expected namespace,key,value`)
      return
    }
    const [ns, key, value] = [cols[0].trim(), cols[1].trim(), cols[2]]
    if (!KNOWN_NS.has(ns)) {
      parseErrors.push(`Line ${line}: unknown namespace "${ns}"`)
      return
    }
    if (!key) {
      parseErrors.push(`Line ${line}: missing key`)
      return
    }
    ;(byNamespace[ns] ??= {})[key] = value
  })

  if (parseErrors.length > 0) return { ok: false, messages: parseErrors }

  const saveErrors: string[] = []
  let savedTerms = 0
  let savedNamespaces = 0
  for (const [ns, values] of Object.entries(byNamespace)) {
    try {
      await terminologyApi.saveOverrides(locale, ns as Namespace, values as never)
      savedTerms += Object.keys(values).length
      savedNamespaces += 1
    } catch (err) {
      saveErrors.push(`${ns}: ${err instanceof Error ? err.message : 'save failed'}`)
    }
  }

  // The per-namespace saves are not atomic. If any namespace persisted we MUST
  // reload resources so the UI reflects the partial write; reporting partial
  // failure lets callers refresh state instead of pretending nothing changed.
  if (savedNamespaces > 0) await i18n.reloadResources(locale)

  if (saveErrors.length > 0) {
    return {
      ok: false,
      partial: savedNamespaces > 0,
      messages:
        savedNamespaces > 0
          ? [
              `Imported ${savedTerms} terms across ${savedNamespaces} namespaces, then failed:`,
              ...saveErrors,
            ]
          : saveErrors,
    }
  }

  return {
    ok: true,
    messages: [
      `Imported ${savedTerms} terms across ${savedNamespaces} namespaces.`,
    ],
  }
}
